/**
 * <region-banner> — "which country would you like to shop in?" region picker.
 * See sections/region-banner.liquid.
 *
 * TWO memories, deliberately different (this is the Orbe / Mango model — see
 * docs/00-features/localization/region-reference-sites.md):
 *
 *   1. CHOICE  — written when the shopper commits a region (banner ACCEPT, or the footer selector).
 *                Lives in localStorage. If it exists, the banner NEVER shows again. This is the
 *                strong "I've decided". Written by assets/region-dropdown.js, read here.
 *   2. DISMISS — written when they click the X. A weak "not right now". Session-scoped by default,
 *                and keyed BY TARGET COUNTRY so saying no to Canada doesn't mute a future UK nudge.
 *
 * The X is NOT the "no" button — picking your country and hitting ACCEPT is. That's why the
 * dismissal is allowed to be short-lived.
 *
 * ACCEPT itself needs no JS: it's a native type="submit" inside the localization form.
 *
 * ⚠️ The form + select live INSIDE the <template>, an inert DocumentFragment — this.querySelector()
 * will NOT find them. Only query the CLONED fragment.
 *
 * Fail-closed: any fetch/parse problem -> no banner. On the local `shopify theme dev` proxy the
 * suggestions request 401s, so the banner can't appear there.
 * DEV: ?region_banner_preview=CA forces it open (bypasses geolocation, dismissal AND choice).
 */
(function () {
  if (customElements.get('region-banner')) return;

  var DISMISS_KEY = 'ss-region-dismissed'; // { "CA": <ts>, "GB": <ts> }
  var CHOICE_KEY = 'ss-region-choice';     // { iso: "CA", ts: <ts> }  (shared with region-dropdown.js)
  var DAY_MS = 86400000;

  class RegionBanner extends HTMLElement {
    connectedCallback() {
      this.content = this.querySelector('[data-region-banner-content]');
      this.template = this.querySelector('[data-region-banner-template]');
      if (!this.content || !this.template) return;

      this.current = (this.getAttribute('data-current-country') || '').toUpperCase();
      this.root = this.getAttribute('data-root') || '/';
      this.dismissMode = this.getAttribute('data-dismiss-mode') || 'session';
      this.dismissDays = parseInt(this.getAttribute('data-dismiss-days'), 10) || 7;
      this.choiceDays = parseInt(this.getAttribute('data-choice-days'), 10) || 0; // 0 = forever
      this.available = this.readAvailable();

      // THEME EDITOR. Geolocation can't match in here, so a merchant editing the copy would stare at
      // a blank canvas. Show it while the section is selected in the sidebar instead, and ignore both
      // memories entirely — otherwise one dismissal would lock them out of their own section.
      // This branch REPLACES the storefront logic; it never geolocates.
      if (window.Shopify && window.Shopify.designMode) {
        this.initDesignMode();
        return;
      }

      var forced = new URLSearchParams(window.location.search).get('region_banner_preview');
      if (forced) {
        this.show(forced.toUpperCase());
        return;
      }

      if (this.hasChosen()) return;                  // they've decided — never nudge again
      if (!Object.keys(this.available).length) return;
      if (this.allTargetsDismissed()) return;        // skip the network call if nothing could show

      this.detect();
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:section:select', this._onEditorSelect);
      document.removeEventListener('shopify:section:deselect', this._onEditorDeselect);
    }

    /* ---------------- Theme editor ---------------- */

    initDesignMode() {
      var self = this;
      var wrapperId = 'shopify-section-' + (this.getAttribute('data-section-id') || '');

      // `shopify:section:select` ALSO re-fires after a re-render while the section is still selected
      // (event.detail.load === true). That's what keeps the popup on screen as the merchant types,
      // instead of it vanishing on every keystroke that re-renders the section.
      this._onEditorSelect = function (e) {
        if (e.target.id === wrapperId) self.show(self.previewIso());
      };
      this._onEditorDeselect = function (e) {
        if (e.target.id === wrapperId) self.hide();
      };

      document.addEventListener('shopify:section:select', this._onEditorSelect);
      document.addEventListener('shopify:section:deselect', this._onEditorDeselect);
    }

    // Which country to pre-select in the editor: the first available target (i.e. CA), else current.
    previewIso() {
      return Object.keys(this.available)[0] || this.current;
    }

    // The TARGET countries that are actually available (iso -> true). Decides whether to show at all.
    readAvailable() {
      var map = {};
      var nodes = this.querySelectorAll('[data-region-country-data] [data-iso]');
      for (var i = 0; i < nodes.length; i++) {
        map[nodes[i].getAttribute('data-iso').toUpperCase()] = true;
      }
      return map;
    }

    /* ---------------- Memory 1: the CHOICE (strong) ---------------- */

    // Has the shopper explicitly committed a region? Written by region-dropdown.js on submit —
    // so it covers BOTH the banner's ACCEPT and the footer selector.
    hasChosen() {
      try {
        var saved = JSON.parse(window.localStorage.getItem(CHOICE_KEY));
        if (!saved || !saved.ts) return false;
        if (!this.choiceDays) return true; // 0 = remember forever
        return (Date.now() - saved.ts) / DAY_MS < this.choiceDays;
      } catch (e) {
        return false;
      }
    }

    /* ---------------- Memory 2: the DISMISS (weak) ---------------- */

    // Session mode uses sessionStorage, so it dies with the tab — no timestamp maths needed.
    get dismissStore() {
      return this.dismissMode === 'session' ? window.sessionStorage : window.localStorage;
    }

    readDismissals() {
      try {
        return JSON.parse(this.dismissStore.getItem(DISMISS_KEY)) || {};
      } catch (e) {
        return {};
      }
    }

    isDismissed(iso) {
      var ts = this.readDismissals()[iso];
      if (!ts) return false;
      if (this.dismissMode === 'session') return true;
      return (Date.now() - ts) / DAY_MS < this.dismissDays;
    }

    allTargetsDismissed() {
      for (var iso in this.available) {
        if (!this.isDismissed(iso)) return false;
      }
      return true;
    }

    dismiss(iso) {
      try {
        var map = this.readDismissals();
        map[iso] = Date.now();
        this.dismissStore.setItem(DISMISS_KEY, JSON.stringify(map));
      } catch (e) {
        /* ignore storage failures */
      }
      this.hide();
    }

    // Just the animation. Split out of dismiss() so the theme editor can hide it on deselect WITHOUT
    // writing a dismissal — a merchant clicking away from the section isn't saying "not now".
    hide() {
      this.classList.remove('is-visible');
      var self = this;
      setTimeout(function () { self.hidden = true; }, 300); // leave the a11y tree after the fade
    }

    /* ---------------- Detection + render ---------------- */

    detect() {
      var url = this.root.replace(/\/$/, '') + '/browsing_context_suggestions.json';
      var self = this;
      fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          var iso = self.detectedIso(data);
          if (!iso || iso === self.current) return;
          if (!self.available[iso]) return;   // not an available target
          if (self.isDismissed(iso)) return;  // dismissed THIS country specifically
          self.show(iso);
        })
        .catch(function () { /* fail closed */ });
    }

    detectedIso(data) {
      if (!data) return null;
      var d = data.detected_values || data.detection || data.detected || data;
      var c = d && d.country;
      if (!c) return null;
      var iso = typeof c === 'string' ? c : c.handle || c.iso_code || c.code || c.name;
      return iso ? String(iso).toUpperCase() : null;
    }

    show(iso) {
      // Re-entrant: the editor calls this on every select (incl. after each re-render), so wipe the
      // previous clone first or they stack up.
      this.content.innerHTML = '';

      var frag = this.template.content.cloneNode(true);

      // Pre-select the detected country. region-dropdown mirrors this into its trigger on upgrade.
      var select = frag.querySelector('[data-region-select]');
      if (select) {
        for (var o = 0; o < select.options.length; o++) {
          if (select.options[o].value.toUpperCase() === iso) {
            select.selectedIndex = o;
            break;
          }
        }
      }

      var self = this;
      var dismissEls = frag.querySelectorAll('[data-region-dismiss]');
      for (var i = 0; i < dismissEls.length; i++) {
        dismissEls[i].addEventListener('click', function () { self.dismiss(iso); });
      }

      this.content.appendChild(frag);
      this.hidden = false;
      requestAnimationFrame(function () { self.classList.add('is-visible'); });
    }
  }

  customElements.define('region-banner', RegionBanner);
})();
